import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

/**
 * Sitewide scroll-reveal system.
 *
 * Any element marked `data-scroll-reveal` gets an `.is-revealed` class the first time it
 * crosses the viewport threshold. A single shared `IntersectionObserver` handles every
 * instance on the page (rather than one observer per section) to keep this cheap on
 * content-heavy pages. All actual motion is CSS-driven (opacity/transform only, see
 * base.css) so this module only ever toggles a class.
 *
 * Respects `prefers-reduced-motion` unconditionally, regardless of the merchant's
 * `scroll_reveal_enabled` setting, and skips itself on low-power devices, matching the
 * heuristics already used by view-transitions.js / utilities.js.
 */

const REVEALED_CLASS = 'is-revealed';
const ROOT_MARGIN = '0px 0px -10% 0px';
const THRESHOLD = 0.1;

/** @type {IntersectionObserver | null} */
let observer = null;

function shouldRevealInstantly() {
  return (
    prefersReducedMotion() ||
    isLowPowerDevice() ||
    !document.documentElement.hasAttribute('data-scroll-reveal-style')
  );
}

/**
 * @param {Element} element
 */
export function observe(element) {
  if (element.classList.contains(REVEALED_CLASS)) return;

  if (shouldRevealInstantly()) {
    element.classList.add(REVEALED_CLASS);
    return;
  }

  (observer ??= createObserver()).observe(element);
}

function createObserver() {
  return new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        entry.target.classList.add(REVEALED_CLASS);
        obs.unobserve(entry.target);
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: THRESHOLD }
  );
}

function observeAll(root = document) {
  for (const element of root.querySelectorAll('[data-scroll-reveal]')) {
    observe(element);
  }
}

function init() {
  observeAll();

  // Section Rendering API updates (facets, cart, recommendations, etc.) can inject new
  // markup after first paint — pick up any new [data-scroll-reveal] elements as they land.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches('[data-scroll-reveal]')) observe(node);
        observeAll(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
